import { DropzoneFile } from 'components/Dropzone/utils';
import { Phone } from 'phoneNumberUtils';
import { ObjectValues } from 'types';
import { EnterpriseMember } from './Enterprises';
import { SessionScope } from './Message';

export const USER_TYPE = {
  SUPER_ADMIN: 'Super admin',
  ADMIN: 'Admin',
  USER: 'User',
} as const;

export type Role = ObjectValues<typeof USER_TYPE>;

export const USER_SEARCH_PREFERENCE = {
  ANY: 'any',
  SPECIFIC: 'specific',
} as const;

type UserSearchPreferenceMode = ObjectValues<typeof USER_SEARCH_PREFERENCE>;

type AccredititationDetails = 'CA' | 'CPA' | 'IPAA';

type AccreditedInvestor = 'Yes' | 'No' | 'Unsure';

type ClientDetails = {
  firstName: string;
  lastName: string;
  email: string;
  mobilePhoneNumber: string;
  location: string;
  companyEmployer: string;
};

type AccountantDetails = {
  firstName: string;
  lastName: string;
  firm: string;
  email: string;
  phone: string;
  city: string;
  confirmKnowsClient: boolean;
};

type CompanyDetails = {
  ACN: string;
  name: string;
};

export type Accountant = {
  _id: string;
  briefNote: string;
  formStatus: 'not_sent' | 'pending' | 'completed';
  sentDate: Date;
  completedDate: Date;
  clientDetails: Partial<ClientDetails>;
  accountantDetails: Partial<AccountantDetails>;
  accreditationDetails: AccredititationDetails[];
  membershipNumber: string;
  accreditedInvestor: AccreditedInvestor;
  directorOfCompanies: CompanyDetails[];
  createAccountOnResolve: boolean;
};

export const PREFERRED_REVENUE_MODELS = {
  RECURRING: 'recurring',
  CONTRACT: 'contract',
  COMMISSION: 'commission',
  AD_HOC_SALES: 'ad_hoc_sales',
} as const;

export type PreferredRevenueModels = ObjectValues<
  typeof PREFERRED_REVENUE_MODELS
>;

export const MANAGEMENT_TEAM = {
  UNDER_MANAGEMENT: 'under_management',
  KEY_ROLE: 'key_role',
  SUCCESSION_PLAN: 'succession plan',
} as const;

export type ManagementTeam = ObjectValues<typeof MANAGEMENT_TEAM>;

export const GM_IN_PLACE = {
  PREFERRED: 'preferred',
  REQUIRED: 'required',
} as const;

export type GmInPlace = ObjectValues<typeof GM_IN_PLACE>;

export const ACQUISITION_TIMELINE = {
  ASAP: 'asap',
  WITHIN_ONE_YEAR: 'within_one_year',
  ONE_TO_THREE_YEARS: 'one_to_three_years',
  THREE_TO_FIVE_YEARS: 'three_to_five_years',
  NA: 'na',
} as const;

export type AcquistionTimeline = ObjectValues<typeof ACQUISITION_TIMELINE>;

export const POLL_NOTIFICATION_INTENSITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;

export type PollNotificationIntensity = ObjectValues<
  typeof POLL_NOTIFICATION_INTENSITY
>;

export const VIEW_SELECT = {
  TABLE: 'isGridViewForDeals',
  GRID: 'isGridViewForLeads',
} as const;

export type ViewSelectProp = ObjectValues<typeof VIEW_SELECT>;

export type UserViews = {
  [VIEW_SELECT.TABLE]?: boolean;
  [VIEW_SELECT.GRID]?: boolean;
};

export const TABLES_WITH_COLUMN_SETTINGS = {
  LEAD: 'LEAD',
  BUYER: 'BUYER',
  PROSPECTIVE_BUYER: 'PROSPECTIVE_BUYER',
  DATA_ROOM_INVITATIONS: 'DATA_ROOM_INVITATIONS',
  SELLER: 'SELLER',
  FORENSICS: 'FORENSICS',
  SUGGESTED_ACTIONS: 'SUGGESTED_ACTIONS',
  CONTACTS: 'CONTACTS',
  BROKER_LIST: 'BROKER_LIST',
  LISTING_METRICS: 'LISTING_METRICS',
  DEAL_METRICS: 'DEAL_METRICS',
  BOSS_MODE_LIST: 'BOSS_MODE_LIST',
  ENTERPRISES_LIST: 'ENTERPRISES_LIST',
  ENTERPRISE_DEALS: 'ENTERPRISE_DEALS',
  ENTERPRISE_TEAM: 'ENTERPRISE_TEAM',
  ENTERPRISE_MANAGE_PEOPLE: 'ENTERPRISE_MANAGE_PEOPLE',
  ENTERPRISE_DATA_ROOMS: 'ENTERPRISE_DATA_ROOMS',
  ENTERPRISE_TOP_BROKERS: 'ENTERPRISE_TOP_BROKERS',
  BUY_SIDE_MEMBERS: 'BUY_SIDE_MEMBERS',
  SELLER_CONNECT_LIST: 'SELLER_CONNECT_LIST',
} as const;

export type TableColumnSettingsKey = ObjectValues<
  typeof TABLES_WITH_COLUMN_SETTINGS
>;

export const HIDE_TABLE_COLUMNS = {
  [TABLES_WITH_COLUMN_SETTINGS.LEAD]: 'hideLeadColumns',
  [TABLES_WITH_COLUMN_SETTINGS.BUYER]: 'hideBuyerPortfolioColumns',
  [TABLES_WITH_COLUMN_SETTINGS.PROSPECTIVE_BUYER]:
    'hideProspectiveBuyerColumns',
  [TABLES_WITH_COLUMN_SETTINGS.DATA_ROOM_INVITATIONS]:
    'hideDataRoomInvitationsColumns',
  [TABLES_WITH_COLUMN_SETTINGS.SELLER]: 'hideSellerPortfolioColumns',
  [TABLES_WITH_COLUMN_SETTINGS.FORENSICS]: 'hideForensicsColumns',
  [TABLES_WITH_COLUMN_SETTINGS.SUGGESTED_ACTIONS]:
    'hideSuggestedActionsColumns',
  [TABLES_WITH_COLUMN_SETTINGS.CONTACTS]: 'hideContactsColumns',
  [TABLES_WITH_COLUMN_SETTINGS.BROKER_LIST]: 'hideBrokerListColumns',
  [TABLES_WITH_COLUMN_SETTINGS.LISTING_METRICS]: 'hideListingMetricsColumns',
  [TABLES_WITH_COLUMN_SETTINGS.DEAL_METRICS]: 'hideDealMetricsColumns',
  [TABLES_WITH_COLUMN_SETTINGS.BOSS_MODE_LIST]: 'hideBossModeListColumns',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISES_LIST]: 'hideEnterprisesListColumns',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_DEALS]: 'hideEnterpriseDealsColumns',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_TEAM]: 'hideEnterpriseTeamColumns',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_MANAGE_PEOPLE]:
    'hideEnterpriseManagePeopleColumns',
  [TABLES_WITH_COLUMN_SETTINGS.BUY_SIDE_MEMBERS]: 'hideBuySideMembersColumns',
  [TABLES_WITH_COLUMN_SETTINGS.SELLER_CONNECT_LIST]:
    'hideSellerConnectListColumns',
} as const;

export const REORDER_TABLE_COLUMNS = {
  [TABLES_WITH_COLUMN_SETTINGS.LEAD]: 'leadColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.BUYER]: 'buyerPortfolioColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.PROSPECTIVE_BUYER]:
    'prospectiveBuyerColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.DATA_ROOM_INVITATIONS]:
    'dataRoomInvitationsColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.SELLER]: 'sellerPortfolioColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.FORENSICS]: 'forensicsColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.SUGGESTED_ACTIONS]:
    'suggestedActionsColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.CONTACTS]: 'contactsColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.BROKER_LIST]: 'brokerListColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.LISTING_METRICS]: 'listingMetricsColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.DEAL_METRICS]: 'dealMetricsColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.BOSS_MODE_LIST]: 'bossModeListColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISES_LIST]: 'enterprisesListColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_DEALS]: 'enterpriseDealsColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_TEAM]: 'enterpriseTeamColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_MANAGE_PEOPLE]:
    'enterpriseManagePeopleColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_DATA_ROOMS]:
    'enterpriseDataRoomsColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_TOP_BROKERS]:
    'enterpriseTopBrokersColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.BUY_SIDE_MEMBERS]: 'buySideMembersColumnOrder',
  [TABLES_WITH_COLUMN_SETTINGS.SELLER_CONNECT_LIST]:
    'sellerConnectListColumnOrder',
} as const;

export const RESIZE_TABLE_COLUMNS = {
  [TABLES_WITH_COLUMN_SETTINGS.LEAD]: 'leadColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.BUYER]: 'buyerPortfolioColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.PROSPECTIVE_BUYER]:
    'prospectiveBuyerColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.DATA_ROOM_INVITATIONS]:
    'dataRoomInvitationsColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.SELLER]: 'sellerPortfolioColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.FORENSICS]: 'forensicsColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.SUGGESTED_ACTIONS]:
    'suggestedActionsColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.CONTACTS]: 'contactsColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.BROKER_LIST]: 'brokerListColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.LISTING_METRICS]: 'listingMetricsColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.DEAL_METRICS]: 'dealMetricsColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.BOSS_MODE_LIST]: 'bossModeListColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISES_LIST]: 'enterprisesListColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_DEALS]: 'enterpriseDealsColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_TEAM]: 'enterpriseTeamColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_MANAGE_PEOPLE]:
    'enterpriseManagePeopleColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_DATA_ROOMS]:
    'enterpriseDataRoomsColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_TOP_BROKERS]:
    'enterpriseTopBrokersColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.BUY_SIDE_MEMBERS]: 'buySideMembersColumnWidths',
  [TABLES_WITH_COLUMN_SETTINGS.SELLER_CONNECT_LIST]:
    'sellerConnectListColumnWidths',
} as const;

export type TableSettings = {
  // lead
  [HIDE_TABLE_COLUMNS.LEAD]: string[];
  [REORDER_TABLE_COLUMNS.LEAD]: string[];
  [RESIZE_TABLE_COLUMNS.LEAD]: Record<string, number>;

  // buyer portfolio
  [HIDE_TABLE_COLUMNS.BUYER]: string[];
  [REORDER_TABLE_COLUMNS.BUYER]: string[];
  [RESIZE_TABLE_COLUMNS.BUYER]: Record<string, number>;

  // prospective buyer (listing invitations)
  [HIDE_TABLE_COLUMNS.PROSPECTIVE_BUYER]: string[];
  [REORDER_TABLE_COLUMNS.PROSPECTIVE_BUYER]: string[];
  [RESIZE_TABLE_COLUMNS.PROSPECTIVE_BUYER]: Record<string, number>;

  // data room invitations
  [HIDE_TABLE_COLUMNS.DATA_ROOM_INVITATIONS]: string[];
  [REORDER_TABLE_COLUMNS.DATA_ROOM_INVITATIONS]: string[];
  [RESIZE_TABLE_COLUMNS.DATA_ROOM_INVITATIONS]: Record<string, number>;

  // seller portfolio
  [HIDE_TABLE_COLUMNS.SELLER]: string[];
  [REORDER_TABLE_COLUMNS.SELLER]: string[];
  [RESIZE_TABLE_COLUMNS.SELLER]: Record<string, number>;

  // BM forensics
  [HIDE_TABLE_COLUMNS.FORENSICS]: string[];
  [REORDER_TABLE_COLUMNS.FORENSICS]: string[];
  [RESIZE_TABLE_COLUMNS.FORENSICS]: Record<string, number>;

  // BM suggested actions
  [HIDE_TABLE_COLUMNS.SUGGESTED_ACTIONS]: string[];
  [REORDER_TABLE_COLUMNS.SUGGESTED_ACTIONS]: string[];
  [RESIZE_TABLE_COLUMNS.SUGGESTED_ACTIONS]: Record<string, number>;

  // contacts
  [HIDE_TABLE_COLUMNS.CONTACTS]: string[];
  [REORDER_TABLE_COLUMNS.CONTACTS]: string[];
  [RESIZE_TABLE_COLUMNS.CONTACTS]: Record<string, number>;

  // broker list
  [HIDE_TABLE_COLUMNS.BROKER_LIST]: string[];
  [REORDER_TABLE_COLUMNS.BROKER_LIST]: string[];
  [RESIZE_TABLE_COLUMNS.BROKER_LIST]: Record<string, number>;

  // listing metrics
  [HIDE_TABLE_COLUMNS.LISTING_METRICS]: string[];
  [REORDER_TABLE_COLUMNS.LISTING_METRICS]: string[];
  [RESIZE_TABLE_COLUMNS.LISTING_METRICS]: Record<string, number>;

  // deal metrics
  [HIDE_TABLE_COLUMNS.DEAL_METRICS]: string[];
  [REORDER_TABLE_COLUMNS.DEAL_METRICS]: string[];
  [RESIZE_TABLE_COLUMNS.DEAL_METRICS]: Record<string, number>;

  // boss mode list
  [HIDE_TABLE_COLUMNS.BOSS_MODE_LIST]: string[];
  [REORDER_TABLE_COLUMNS.BOSS_MODE_LIST]: string[];
  [RESIZE_TABLE_COLUMNS.BOSS_MODE_LIST]: Record<string, number>;

  // enterprises list
  [HIDE_TABLE_COLUMNS.ENTERPRISES_LIST]: string[];
  [REORDER_TABLE_COLUMNS.ENTERPRISES_LIST]: string[];
  [RESIZE_TABLE_COLUMNS.ENTERPRISES_LIST]: Record<string, number>;

  // enterprise deals
  [HIDE_TABLE_COLUMNS.ENTERPRISE_DEALS]: string[];
  [REORDER_TABLE_COLUMNS.ENTERPRISE_DEALS]: string[];
  [RESIZE_TABLE_COLUMNS.ENTERPRISE_DEALS]: Record<string, number>;

  // enterprise team
  [HIDE_TABLE_COLUMNS.ENTERPRISE_TEAM]: string[];
  [REORDER_TABLE_COLUMNS.ENTERPRISE_TEAM]: string[];
  [RESIZE_TABLE_COLUMNS.ENTERPRISE_TEAM]: Record<string, number>;

  // enterprise manage people
  [HIDE_TABLE_COLUMNS.ENTERPRISE_MANAGE_PEOPLE]: string[];
  [REORDER_TABLE_COLUMNS.ENTERPRISE_MANAGE_PEOPLE]: string[];
  [RESIZE_TABLE_COLUMNS.ENTERPRISE_MANAGE_PEOPLE]: Record<string, number>;

  // data room buy-side members
  [HIDE_TABLE_COLUMNS.BUY_SIDE_MEMBERS]: string[];
  [REORDER_TABLE_COLUMNS.BUY_SIDE_MEMBERS]: string[];
  [RESIZE_TABLE_COLUMNS.BUY_SIDE_MEMBERS]: Record<string, number>;

  // BM seller connect list
  [HIDE_TABLE_COLUMNS.SELLER_CONNECT_LIST]: string[];
  [REORDER_TABLE_COLUMNS.SELLER_CONNECT_LIST]: string[];
  [RESIZE_TABLE_COLUMNS.SELLER_CONNECT_LIST]: Record<string, number>;

  // tables without manage columns
  // enterprise data rooms
  [REORDER_TABLE_COLUMNS.ENTERPRISE_DATA_ROOMS]: string[];
  [RESIZE_TABLE_COLUMNS.ENTERPRISE_DATA_ROOMS]: Record<string, number>;

  // enterprise top brokers
  [REORDER_TABLE_COLUMNS.ENTERPRISE_TOP_BROKERS]: string[];
  [RESIZE_TABLE_COLUMNS.ENTERPRISE_TOP_BROKERS]: Record<string, number>;
};

export type User = {
  _id: string;

  socialLogin?: boolean;

  hasAccessedPlatform?: boolean; // user has access platform at least once

  email?: string;
  hasVerifiedEmail?: boolean;

  phone?: Phone;
  contactNumber?: string;
  hasVerifiedContactNumber?: boolean;
  isContactNumberVerifiedElsewhere?: boolean;

  role: Role;

  firstName: string;
  lastName: string;

  title?: string;

  linkedinAccount: string;

  profileImage: DropzoneFile;

  unreadSessionIds: string[];
  allUnreadSessionsCount: number;
  allSessionsCount: number;
  mineSessionsCount: number;

  lastSeen?: Date;

  accountant?: Partial<Accountant>;

  enterpriseMember?: EnterpriseMember;

  snoozedDealRoomBannerNotification?: boolean;

  leadTableColumns: string[];

  // company section in profile
  isBusinessBroker?: boolean;
  companyName?: string;
  companyABN?: string;
  firmLogo?: DropzoneFile;

  // for portfolio
  hasBuyingDeals?: boolean;
  hasSellingDeals?: boolean;
  hasSellingUrgentDeals?: boolean;

  // in LCW
  canSkipPayment?: boolean;

  // Profile tab
  // Profile tab - Acquistion preferences
  hasAcquisitionPreferences: boolean;

  // Profile tab - Location
  preferredLocationMode: UserSearchPreferenceMode;
  preferredLocations: Array<string>;

  preferredBudgetMode: UserSearchPreferenceMode;
  budgetMinPrice: number;
  budgetMaxPrice: number;

  preferredIndustryIncludeMode: UserSearchPreferenceMode;
  preferredIncludeIndustries: Array<string>;

  preferredIndustryExcludeMode: UserSearchPreferenceMode;
  preferredExcludeIndustries: Array<string>;

  // Profile tab - Financials
  ebitda: number;
  revenueMin: number;
  ebitdaMin: number;
  ebitdaMax: number;
  preferredRevenueModels: PreferredRevenueModels[];

  // Profile tab - Advanced preferences
  managementTeam: ManagementTeam;
  gmInPlace: GmInPlace;
  acquistionTimeline: AcquistionTimeline;

  interestedInMoreBusinesses: boolean;
  interestedInCommercialProperty: boolean;

  // Settings tab
  hasActiveDeleteRequestAccount?: boolean;
  isSubscribedToNewsletter?: boolean;

  // Notification tab
  pollNotificationIntensity: PollNotificationIntensity | null;
  enablePollEmailNotifications: boolean;
  enablePollSmsNotifications: boolean;

  promotedAt?: Date;
  createdAt?: Date;

  hasRemindedToVerifyPhone?: boolean; // show Verify phone number if false

  fileReorderViewMode: FileReorderViewMode;

  avatarColor: AvatarColors;
} & TableSettings &
  UserViews &
  UserPreferences;

export type BasicUserInfo = Pick<
  User,
  | 'firstName'
  | 'lastName'
  | 'title'
  | 'profileImage'
  | 'linkedinAccount'
  | 'phone'
  | 'companyName'
>;

export type IdentityUserInfo = Pick<
  User,
  'firstName' | 'lastName' | 'email'
> & { colleagueId?: string };

export const FILE_REORDER_VIEW_MODE = {
  LEAD: 'lead',
  FILE: 'file',
} as const;

export type FileReorderViewMode = ObjectValues<typeof FILE_REORDER_VIEW_MODE>;

export type UserPreferences = {
  doesNotShowDataRoomSellSideOnboarding?: boolean;
  doesNotShowDataRoomBuySideOnboarding?: boolean;
  onboardingCards?: OnboardingCards;

  seenAnalyticsExportFeature?: boolean;
  seenSecureLinkAnalyticsFeature?: boolean;
  seenEnterpriseUsageReportFeature?: boolean;

  seenEnterpriseOverview?: boolean;

  seenGroupRowFeature?: boolean;
  seenGroupNameFeature?: boolean;

  seenAutomationFlow?: boolean;

  messagingSessionsScope?: SessionScope;

  hideReplySuggestions?: boolean;
};

export const AVATAR_COLORS = [
  '#4F46E5',
  '#0EA5E9',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
] as const;

export type AvatarColors = typeof AVATAR_COLORS[number];

export const ONBOARDING_CARD_KEY = {
  SELL_SIDE_DASHBOARD: 'sellSideDashboard',
  SELL_SIDE_FILES: 'sellSideFiles',
  SELL_SIDE_QA: 'sellSideQA',
  SELL_SIDE_MEMBERS: 'sellSideMembers',
  SELL_SIDE_WAITING_ROOM: 'sellSideWaitingRoom',
  SELL_SIDE_SECURE_LINKS: 'sellSideSecureLinks',
  SELL_SIDE_FILE_REQUESTS: 'sellSideFileRequests',
  SELL_SIDE_ANALYTICS: 'sellSideAnalytics',
  SELL_SIDE_NDAS: 'sellSideNDAs',
  SELL_SIDE_FILE_SECURITY: 'sellSideFileSecurity',
  SELL_SIDE_SETTINGS: 'sellSideSettings',
  BUY_SIDE_FILES: 'buySideFiles',
  BUY_SIDE_QA: 'buySideQA',
  BUY_SIDE_MEMBERS: 'buySideMembers',
} as const;

export type OnboardingCardKey = ObjectValues<typeof ONBOARDING_CARD_KEY>;

export const ONBOARDING_CARD_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  NEW_VERSION: 'new_version',
} as const;

export type OnboardingCardStatus = ObjectValues<typeof ONBOARDING_CARD_STATUS>;

export type OnboardingCards = Partial<
  Record<OnboardingCardKey, OnboardingCardStatus>
>;
